import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";

export type SettingsSub = "mail" | "payment" | "membership" | "security";

type MembershipSettingsFeature = {
  key: string;
  label: string;
  includedInFree: boolean;
  includedInPlus: boolean;
  includedInPro: boolean;
};

export type Settings = {
  payment: {
    ibanLabel: string;
    iban: string;
    accountName: string;
    note: string;
  };
  email: {
    fromName: string;
    fromEmail: string;
    bccAdmin?: boolean;
    retryMaxAttempts?: number;
  };
  branding?: {
    instagram?: string;
    phone?: string;
    website?: string;
  };
  ftp?: {
    host?: string;
    user?: string;
    password?: string;
    port?: number;
    secure?: boolean;
    baseDir?: string;
    publicBaseUrl?: string;
  };
  membership?: {
    features: MembershipSettingsFeature[];
  };
  delivery: {
    autoSendEnabled: boolean;
    minutes: {
      pin: number;
      isim: number;
      normal: number;
      premium: number;
      premiumRush: number;
      iliski: number;
    };
  };
};

type SettingsTabProps = {
  settings: Settings;
  settingsSub: SettingsSub;
  onChange: (updater: (prev: Settings) => Settings) => void;
  onSave: () => void;
  isSaving: boolean;
  error?: string;
  passwordStatus?: {
    hasPassword: boolean;
    managedIn: "database" | "env" | "none";
  };
  onChangeAdminPassword?: (args: { currentPassword: string; newPassword: string; confirmPassword: string }) => void;
  isChangingPassword?: boolean;
  passwordError?: string;
  passwordSuccess?: string;
};

export function SettingsTab({
  settings,
  settingsSub,
  onChange,
  onSave,
  isSaving,
  error,
  passwordStatus,
  onChangeAdminPassword,
  isChangingPassword,
  passwordError,
  passwordSuccess,
}: SettingsTabProps) {
  const showPaymentSection = settingsSub === "payment";
  const showMembershipSection = settingsSub === "membership";
  const showSecuritySection = settingsSub === "security";
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  return (
    <Card className="p-5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-sm font-semibold">
            {showSecuritySection
              ? "Güvenlik"
              : showMembershipSection
                ? "Üyelik Planları"
                : showPaymentSection
                  ? "Ödeme Ayarları"
                  : "Mail Ayarları"}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {showSecuritySection
              ? "Yönetim paneli giriş şifresini güvenli şekilde güncelleyin."
              : showMembershipSection
                ? "Free / Plus / Pro planlarında hangi özelliklerin açık olacağını belirleyin."
                : showPaymentSection
                  ? "Havale/EFT bilgilerini burada güncelleyebilirsiniz."
                  : "Gönderim, FTP ve teslimat ayarlarını burada yönetebilirsiniz."}
          </div>
        </div>
        {!showSecuritySection ? (
          <Button className="rounded-full" disabled={isSaving} onClick={onSave}>
            {isSaving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        ) : null}
      </div>

      {error ? (
        <div className="mt-3 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      <Separator className="my-4" />

      <div className="grid gap-4">
        {showSecuritySection ? (
          <>
            <div className="text-sm font-medium">Yönetici şifresi</div>
            <div className="rounded-xl border bg-muted/20 p-3 text-xs text-muted-foreground">
              Durum: {passwordStatus?.hasPassword ? "Aktif" : "Tanımlı değil"} • Kaynak: {passwordStatus?.managedIn === "database" ? "Panel (Veritabanı)" : passwordStatus?.managedIn === "env" ? "Sunucu ENV" : "Tanımsız"}
            </div>

            {passwordSuccess ? (
              <div className="rounded-lg border border-emerald-300/40 bg-emerald-500/10 p-3 text-sm text-emerald-700 dark:text-emerald-300">
                {passwordSuccess}
              </div>
            ) : null}

            {passwordError ? (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                {passwordError}
              </div>
            ) : null}

            <form
              className="grid gap-3"
              onSubmit={(event) => {
                event.preventDefault();
                if (!onChangeAdminPassword) return;
                onChangeAdminPassword({ currentPassword, newPassword, confirmPassword });
              }}
            >
              <div className="grid gap-2">
                <Label htmlFor="admin_current_password">Mevcut şifre</Label>
                <Input
                  id="admin_current_password"
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  disabled={Boolean(isChangingPassword)}
                />
              </div>

              <div className="grid gap-2 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="admin_new_password">Yeni şifre</Label>
                  <Input
                    id="admin_new_password"
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={Boolean(isChangingPassword)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="admin_new_password_confirm">Yeni şifre (tekrar)</Label>
                  <Input
                    id="admin_new_password_confirm"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={Boolean(isChangingPassword)}
                  />
                </div>
              </div>

              <div className="text-xs text-muted-foreground">En az 8 karakter önerilir.</div>

              <div>
                <Button type="submit" className="rounded-full" disabled={Boolean(isChangingPassword)}>
                  {isChangingPassword ? "Güncelleniyor..." : "Şifreyi Güncelle"}
                </Button>
              </div>
            </form>
          </>
        ) : showMembershipSection ? (
          <>
            <div className="text-sm font-medium">Plan özellikleri</div>
            <div className="overflow-hidden rounded-xl border">
              <div className="grid grid-cols-[1.5fr_120px_120px_120px] gap-2 bg-muted/30 px-3 py-2 text-xs font-semibold text-muted-foreground">
                <div>Özellik</div>
                <div className="text-center">Free</div>
                <div className="text-center">Plus</div>
                <div className="text-center">Pro</div>
              </div>

              <div className="divide-y">
                {(settings.membership?.features ?? []).map((feature, index) => (
                  <div key={feature.key} className="grid grid-cols-[1.5fr_120px_120px_120px] items-center gap-2 px-3 py-3">
                    <div className="grid gap-2">
                      <Input
                        value={feature.label}
                        onChange={(e) =>
                          onChange((prev) => {
                            const currentFeatures = prev.membership?.features ?? [];
                            const nextFeatures = currentFeatures.map((row, rowIndex) =>
                              rowIndex === index ? { ...row, label: e.target.value } : row,
                            );
                            return {
                              ...prev,
                              membership: { features: nextFeatures },
                            };
                          })
                        }
                      />
                      <div className="text-[11px] text-muted-foreground">Anahtar: {feature.key}</div>
                    </div>

                    <label className="flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={Boolean(feature.includedInFree)}
                        onChange={(e) =>
                          onChange((prev) => {
                            const currentFeatures = prev.membership?.features ?? [];
                            const nextFeatures = currentFeatures.map((row, rowIndex) =>
                              rowIndex === index ? { ...row, includedInFree: e.target.checked } : row,
                            );
                            return {
                              ...prev,
                              membership: { features: nextFeatures },
                            };
                          })
                        }
                      />
                    </label>

                    <label className="flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={Boolean(feature.includedInPlus)}
                        onChange={(e) =>
                          onChange((prev) => {
                            const currentFeatures = prev.membership?.features ?? [];
                            const nextFeatures = currentFeatures.map((row, rowIndex) =>
                              rowIndex === index ? { ...row, includedInPlus: e.target.checked } : row,
                            );
                            return {
                              ...prev,
                              membership: { features: nextFeatures },
                            };
                          })
                        }
                      />
                    </label>

                    <label className="flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={Boolean(feature.includedInPro)}
                        onChange={(e) =>
                          onChange((prev) => {
                            const currentFeatures = prev.membership?.features ?? [];
                            const nextFeatures = currentFeatures.map((row, rowIndex) =>
                              rowIndex === index ? { ...row, includedInPro: e.target.checked } : row,
                            );
                            return {
                              ...prev,
                              membership: { features: nextFeatures },
                            };
                          })
                        }
                      />
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : showPaymentSection ? (
          <>
            <div className="text-sm font-medium">Ödeme (Havale/EFT)</div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Başlık</Label>
                <Input
                  value={settings.payment.ibanLabel}
                  onChange={(e) =>
                    onChange((prev) => ({
                      ...prev,
                      payment: { ...prev.payment, ibanLabel: e.target.value },
                    }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label>Hesap adı</Label>
                <Input
                  value={settings.payment.accountName}
                  onChange={(e) =>
                    onChange((prev) => ({
                      ...prev,
                      payment: { ...prev.payment, accountName: e.target.value },
                    }))
                  }
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label>IBAN</Label>
              <Input
                value={settings.payment.iban}
                onChange={(e) =>
                  onChange((prev) => ({
                    ...prev,
                    payment: { ...prev.payment, iban: e.target.value },
                  }))
                }
              />
            </div>

            <div className="grid gap-2">
              <Label>Not</Label>
              <Textarea
                value={settings.payment.note}
                onChange={(e) =>
                  onChange((prev) => ({
                    ...prev,
                    payment: { ...prev.payment, note: e.target.value },
                  }))
                }
              />
            </div>
          </>
        ) : (
          <>
            <div className="text-sm font-medium">E-posta</div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Gönderen adı</Label>
                <Input
                  value={settings.email.fromName}
                  onChange={(e) =>
                    onChange((prev) => ({
                      ...prev,
                      email: { ...prev.email, fromName: e.target.value },
                    }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label>Gönderen e-posta</Label>
                <Input
                  value={settings.email.fromEmail}
                  onChange={(e) =>
                    onChange((prev) => ({
                      ...prev,
                      email: { ...prev.email, fromEmail: e.target.value },
                    }))
                  }
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={Boolean(settings.email.bccAdmin)}
                  onChange={(e) =>
                    onChange((prev) => ({
                      ...prev,
                      email: { ...prev.email, bccAdmin: e.target.checked },
                    }))
                  }
                />
                Admin’e BCC
              </label>

              <div className="grid gap-2">
                <Label>Mail retry max</Label>
                <Input
                  type="number"
                  value={settings.email.retryMaxAttempts ?? 5}
                  onChange={(e) =>
                    onChange((prev) => ({
                      ...prev,
                      email: {
                        ...prev.email,
                        retryMaxAttempts: Number(e.target.value || 0),
                      },
                    }))
                  }
                />
              </div>
            </div>

            <Separator className="my-2" />

            <div className="text-sm font-medium">FTP (Tüm görsel yüklemeler)</div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>FTP Host</Label>
                <Input
                  value={settings.ftp?.host ?? ""}
                  onChange={(e) =>
                    onChange((prev) => ({
                      ...prev,
                      ftp: { ...prev.ftp, host: e.target.value },
                    }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label>FTP Kullanıcı</Label>
                <Input
                  value={settings.ftp?.user ?? ""}
                  onChange={(e) =>
                    onChange((prev) => ({
                      ...prev,
                      ftp: { ...prev.ftp, user: e.target.value },
                    }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label>FTP Şifre</Label>
                <Input
                  type="password"
                  value={settings.ftp?.password ?? ""}
                  onChange={(e) =>
                    onChange((prev) => ({
                      ...prev,
                      ftp: { ...prev.ftp, password: e.target.value },
                    }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label>FTP Port</Label>
                <Input
                  type="number"
                  value={Number(settings.ftp?.port ?? 21)}
                  onChange={(e) =>
                    onChange((prev) => ({
                      ...prev,
                      ftp: { ...prev.ftp, port: Number(e.target.value || 21) },
                    }))
                  }
                />
              </div>
              <div className="grid gap-2 sm:col-span-2">
                <Label>Base Klasör</Label>
                <Input
                  value={settings.ftp?.baseDir ?? ""}
                  onChange={(e) =>
                    onChange((prev) => ({
                      ...prev,
                      ftp: { ...prev.ftp, baseDir: e.target.value },
                    }))
                  }
                />
              </div>
              <div className="grid gap-2 sm:col-span-2">
                <Label>Public Base URL</Label>
                <Input
                  value={settings.ftp?.publicBaseUrl ?? ""}
                  onChange={(e) =>
                    onChange((prev) => ({
                      ...prev,
                      ftp: { ...prev.ftp, publicBaseUrl: e.target.value },
                    }))
                  }
                />
              </div>
              <label className="flex items-center gap-2 text-sm sm:col-span-2">
                <input
                  type="checkbox"
                  checked={Boolean(settings.ftp?.secure)}
                  onChange={(e) =>
                    onChange((prev) => ({
                      ...prev,
                      ftp: { ...prev.ftp, secure: e.target.checked },
                    }))
                  }
                />
                FTP Secure (FTPS)
              </label>
            </div>

            <Separator className="my-2" />

            <div className="text-sm font-medium">Teslimat / Otomatik Gönderim</div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={settings.delivery.autoSendEnabled}
                onChange={(e) =>
                  onChange((prev) => ({
                    ...prev,
                    delivery: {
                      ...prev.delivery,
                      autoSendEnabled: e.target.checked,
                    },
                  }))
                }
              />
              Otomatik gönderim açık (planlanan saatte mail + PDF gönder)
            </label>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="grid gap-2">
                <Label>PIN (dk)</Label>
                <Input
                  type="number"
                  value={settings.delivery.minutes.pin}
                  onChange={(e) =>
                    onChange((prev) => ({
                      ...prev,
                      delivery: {
                        ...prev.delivery,
                        minutes: { ...prev.delivery.minutes, pin: Number(e.target.value || 0) },
                      },
                    }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label>İsim (dk)</Label>
                <Input
                  type="number"
                  value={settings.delivery.minutes.isim}
                  onChange={(e) =>
                    onChange((prev) => ({
                      ...prev,
                      delivery: {
                        ...prev.delivery,
                        minutes: { ...prev.delivery.minutes, isim: Number(e.target.value || 0) },
                      },
                    }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label>Normal (dk)</Label>
                <Input
                  type="number"
                  value={settings.delivery.minutes.normal}
                  onChange={(e) =>
                    onChange((prev) => ({
                      ...prev,
                      delivery: {
                        ...prev.delivery,
                        minutes: { ...prev.delivery.minutes, normal: Number(e.target.value || 0) },
                      },
                    }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label>Premium (dk)</Label>
                <Input
                  type="number"
                  value={settings.delivery.minutes.premium}
                  onChange={(e) =>
                    onChange((prev) => ({
                      ...prev,
                      delivery: {
                        ...prev.delivery,
                        minutes: { ...prev.delivery.minutes, premium: Number(e.target.value || 0) },
                      },
                    }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label>Premium Hızlı (dk)</Label>
                <Input
                  type="number"
                  value={settings.delivery.minutes.premiumRush}
                  onChange={(e) =>
                    onChange((prev) => ({
                      ...prev,
                      delivery: {
                        ...prev.delivery,
                        minutes: {
                          ...prev.delivery.minutes,
                          premiumRush: Number(e.target.value || 0),
                        },
                      },
                    }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label>İlişki (dk)</Label>
                <Input
                  type="number"
                  value={settings.delivery.minutes.iliski}
                  onChange={(e) =>
                    onChange((prev) => ({
                      ...prev,
                      delivery: {
                        ...prev.delivery,
                        minutes: { ...prev.delivery.minutes, iliski: Number(e.target.value || 0) },
                      },
                    }))
                  }
                />
              </div>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
